import type {TranslationList} from '@interfaces/i18n';

interface Config {
    isLive: boolean;
    projectHost: string;
    apiNamespace: string;
    cloudPaymentsPublicKey: string;
    vpnCloudPaymentsPublicKey: string;
    agencyCloudPaymentsPublicKey: string;
    papersupportCloudPaymentsPublicKey: string;
    paperturboCloudPaymentsPublicKey: string;
    courseCloudPaymentsPublicKey: string;
    stripePublicKey: string;
    paySelectionSiteId: string;
    paySelectionPublicKey: string;
    kluTezPaymentsPublicKey: string;
    paperVpnTezPaymentsPublicKey: string;
    popupContainerId: string;
    snackbarContainerId: string;
    version?: string;
    customFormSupportEmail: string;
    courseSupportEmail: string;
    paperSupportEmail: string;
    courseManager: string;
    courseTelegram: string;
    courseEmbedHost: string;
    subscribeEmbedHost: string;
    customEmbedHost: string;
    tourEmbedHost: string;
    tosPath: {
        club: TranslationList;
        custom: TranslationList;
        donation: TranslationList;
        course: TranslationList;
    };
    donation: {
        embedHost: string;
        minSum: {
            rub: number;
            eur: number;
            gel: number;
            usd: number;
        };
    };
    vpn: {
        apiNamespace: string;
        endpointNamespace: string;
        embedHost: string;
        profileLink: string;
        supportEmail: string;
        supportTelegramBot: string;
        testGoogleAnalyticsId?: string;
    };
    paperTurbo: {
        apiNamespace: string;
        endpointNamespace: string;
        embedHost: string;
        profileLink: string;
        supportEmail: string;
        testGoogleAnalyticsId?: string;
    };
    klu: {
        apiNamespace: string;
        endpointNamespace: string;
        embedHost: string;
        profileLink: string;
        supportEmail: string;
    };
}

const projectHost = new URL((document.currentScript as HTMLScriptElement).src).origin;

const config: Config = {
    isLive: process.env.IS_LIVE === '1',
    projectHost,
    apiNamespace: `${projectHost}/api`,
    cloudPaymentsPublicKey: process.env.CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    vpnCloudPaymentsPublicKey: process.env.VPN_CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    paperturboCloudPaymentsPublicKey: process.env.PAPERTURBO_CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    agencyCloudPaymentsPublicKey: process.env.AGENCY_CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    papersupportCloudPaymentsPublicKey: process.env.PAPERSUPPORT_CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    courseCloudPaymentsPublicKey: process.env.COURSE_CLOUDPAYMENTS_PUBLIC_KEY ?? '',
    stripePublicKey: process.env.STRIPE_PUBLIC_KEY ?? '',
    paySelectionSiteId: process.env.PAYSELECTION_SITE_ID ?? '',
    paySelectionPublicKey: process.env.PAYSELECTION_PUBLIC_KEY ?? '',
    kluTezPaymentsPublicKey: process.env.KLU_TEZ_PAYMENTS_PUBLIC_KEY ?? '',
    paperVpnTezPaymentsPublicKey: process.env.VPN_TEZ_PAYMENTS_PUBLIC_KEY ?? '',
    popupContainerId: 'popup-root',
    snackbarContainerId: 'snackbar-root',
    version: process.env.VERSION,
    customFormSupportEmail: 'letters@paperpaper.io',
    courseSupportEmail: 'edu@paperpaper.io',
    paperSupportEmail: 'letters@paperpaper.io',
    courseManager: 'https://t.me/asemashkina_wrk',
    courseTelegram: 'https://t.me/papersschool',
    courseEmbedHost: process.env.COURSE_FORM_HOST ?? '',
    subscribeEmbedHost: process.env.SUBSCRIBE_FORM_HOST ?? '',
    customEmbedHost: process.env.CUSTOM_FORM_HOST ?? '',
    tourEmbedHost: process.env.TOUR_FORM_HOST ?? '',
    tosPath: {
        club: {
            en: `${projectHost}/paperpaper_contract_club.pdf`,
            ru: `${projectHost}/paperpaper_contract_club.pdf`,
        },
        custom: {
            en: `${projectHost}/paperpaper_contract.pdf`,
            ru: `${projectHost}/paperpaper_contract.pdf`,
        },
        donation: {
            en: `${projectHost}/papersupport_contract.pdf`,
            ru: `${projectHost}/papersupport_contract.pdf`,
        },
        course: {
            en: `${projectHost}/community_user_agreement.pdf`,
            ru: `${projectHost}/community_user_agreement.pdf`,
        },
    },
    donation: {
        embedHost: process.env.DONATION_FORM_HOST ?? '',
        minSum: {
            rub: 30,
            eur: 5,
            usd: 5,
            gel: 5,
        },
    },
    vpn: {
        apiNamespace: `${projectHost}/vpn/api`,
        endpointNamespace: `${projectHost}/vpn/endpoint`,
        embedHost: process.env.VPN_FORM_HOST ?? '',
        profileLink: process.env.VPN_PROFILE_LINK ?? '',
        supportEmail: 'support@papervpn.io',
        supportTelegramBot: 'https://t.me/papersupport_bot',
        testGoogleAnalyticsId: 'G-YQM61GVPCV',
    },
    paperTurbo: {
        apiNamespace: `${projectHost}/paperturbo/api`,
        endpointNamespace: `${projectHost}/paperturbo/endpoint`,
        embedHost: process.env.PAPERTURBO_FORM_HOST ?? '',
        profileLink: process.env.PAPERTURBO_PROFILE_LINK ?? '',
        supportEmail: 'support@paperturbo.net',
    },
    klu: {
        apiNamespace: `${projectHost}/klu/api`,
        endpointNamespace: `${projectHost}/klu/endpoint`,
        embedHost: process.env.KLU_FORM_HOST ?? '',
        profileLink: process.env.KLU_PROFILE_LINK ?? '',
        supportEmail: 'support@klu.community',
    },
};

export default config;
